import React, { useEffect, useMemo, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import ClientLink from '../components/ClientLink';
import LoanLink from '../components/LoanLink';
import { AuthContext } from '../context/AuthContext';
import { formatDateDMY } from '../utils/dateUtils';
import FullScreenPanel from '../components/UI/FullScreenPanel';
import ResponsiveTable from '../components/ResponsiveTable';
import StatusBadge, { statusClasses } from '../components/UI/StatusBadge';
import { useTableSort } from '../components/UI/tableSort';
import ActionButton from '../components/UI/ActionButton';
import Pager from '../components/UI/Pager';

const CASE_TYPES = ['Complaint', 'Court Case', 'Regulatory Breach', 'Overcharging'];
const STATUSES = ['Open', 'In Progress', 'Resolved', 'Closed', 'Dismissed'];
const SEVERITIES = ['Low', 'Medium', 'High', 'Critical'];
const OUTCOMES = ['Pending', 'In Favour of Entity', 'In Favour of Complainant'];
const RESOLVED_STATUSES = ['Resolved', 'Closed', 'Dismissed'];

const SEVERITY_BADGE = {
  Low: 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300',
  Medium: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
  High: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300',
  Critical: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
};

const EMPTY_FORM = {
  case_type: 'Complaint',
  title: '',
  description: '',
  status: 'Open',
  severity: 'Medium',
  client_id: '',
  loan_id: '',
  amount_involved: '',
  reported_date: '',
  resolved_date: '',
  resolution: '',
  outcome: '',
};

const ComplianceCases = () => {
  const { user } = useContext(AuthContext);
  const perms = Array.isArray(user?.permissions) ? user.permissions : [];
  const canManage = perms.includes('ManageCompliance') || perms.includes('*');

  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ type: '', status: '', q: '' });
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filters.type) params.type = filters.type;
      if (filters.status) params.status = filters.status;
      if (filters.q) params.q = filters.q;
      const res = await axios.get(apiUrl('/api/compliance-cases'), { ...authHeader, params });
      setCases(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load compliance cases');
    } finally {
      setLoading(false);
    }
  }, [filters, authHeader]);

  useEffect(() => {
    const t = setTimeout(fetchCases, 200); // debounce search/filter changes
    return () => clearTimeout(t);
  }, [fetchCases]);

  // The list is filtered server-side; paging is a slice of what came back.
  const totalPages = Math.max(1, Math.ceil(cases.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  // A click on a column sorts every case, then the page is taken.
  const caseSort = useTableSort(cases);
  const pagedCases = caseSort.rows.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  // A new filter should not leave you stranded on a page that no longer exists.
  useEffect(() => {
    setPage(1);
  }, [filters.type, filters.status, filters.q]);

  // Headline counts derived from the loaded list.
  const stats = useMemo(() => {
    const s = {
      total: cases.length,
      open: 0,
      byType: { Complaint: 0, 'Court Case': 0, 'Regulatory Breach': 0, Overcharging: 0 },
      complaintsResolved: 0,
    };
    for (const c of cases) {
      if (!RESOLVED_STATUSES.includes(c.status)) s.open += 1;
      if (s.byType[c.case_type] !== undefined) s.byType[c.case_type] += 1;
      if (c.case_type === 'Complaint' && RESOLVED_STATUSES.includes(c.status)) s.complaintsResolved += 1;
    }
    return s;
  }, [cases]);

  const openNew = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, reported_date: new Date().toISOString().slice(0, 10) });
    setShowForm(true);
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      case_type: c.case_type || 'Complaint',
      title: c.title || '',
      description: c.description || '',
      status: c.status || 'Open',
      severity: c.severity || 'Medium',
      client_id: c.client_code || (c.client_id ?? ''),
      loan_id: c.loan_code || (c.loan_id ?? ''),
      amount_involved: c.amount_involved ?? '',
      reported_date: c.reported_date ? String(c.reported_date).slice(0, 10) : '',
      resolved_date: c.resolved_date ? String(c.resolved_date).slice(0, 10) : '',
      resolution: c.resolution || '',
      outcome: c.outcome || '',
    });
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canManage) { setError('You do not have permission to manage compliance cases.'); return; }
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        // Send the raw ID or code; the server resolves codes (e.g. KFS-CL-000026) to IDs.
        client_id: String(form.client_id || '').trim() || null,
        loan_id: String(form.loan_id || '').trim() || null,
        amount_involved: form.amount_involved === '' ? null : Number(form.amount_involved),
        reported_date: form.reported_date || null,
        resolved_date: form.resolved_date || null,
        outcome: form.case_type === 'Complaint' ? (form.outcome || null) : null,
      };
      if (editingId) {
        await axios.put(apiUrl(`/api/compliance-cases/${editingId}`), payload, authHeader);
      } else {
        await axios.post(apiUrl('/api/compliance-cases'), payload, authHeader);
      }
      cancelForm();
      fetchCases();
    } catch (e2) {
      setError(e2?.response?.data?.error || 'Failed to save compliance case');
    } finally {
      setSaving(false);
    }
  };

  const removeCase = async (c) => {
    if (!canManage) return;
    if (!window.confirm(`Delete case ${c.case_code} — "${c.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(apiUrl(`/api/compliance-cases/${c.id}`), authHeader);
      fetchCases();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to delete case');
    }
  };

  // Inline quick status change from the table.
  const quickStatus = async (c, status) => {
    if (!canManage || status === c.status) return;
    try {
      await axios.put(apiUrl(`/api/compliance-cases/${c.id}`), { status }, authHeader);
      fetchCases();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to update status');
    }
  };

  const fmt = (d) => (d ? formatDateDMY(d) : '—');
  const isResolvedState = RESOLVED_STATUSES.includes(form.status);

  return (
    <div className="max-w-full min-h-screen">
      {canManage && (
        <div className="flex justify-end mb-3">
          <button type="button" onClick={openNew} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded shadow">+ New case</button>
        </div>
      )}

      {error && (
        <div className="p-2 rounded-lg mb-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">{error}</div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
        {[
          { label: 'Total cases', value: stats.total },
          { label: 'Open / unresolved', value: stats.open },
          { label: 'Complaints', value: stats.byType['Complaint'] },
          { label: 'Court cases', value: stats.byType['Court Case'] },
          { label: 'Regulatory breaches', value: stats.byType['Regulatory Breach'] },
          { label: 'Overcharging', value: stats.byType['Overcharging'] },
        ].map((c) => (
          <div key={c.label} className="p-3 rounded-lg bg-white dark:bg-slate-800 shadow border border-gray-100 dark:border-slate-700">
            <div className="text-2xl font-bold text-gray-900 dark:text-slate-100">{c.value}</div>
            <div className="text-xs text-gray-500 dark:text-slate-400">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Create / edit form — popup dialog */}
      {showForm && (
        <FullScreenPanel
          isOpen={showForm}
          onClose={cancelForm}
          title={editingId ? 'Edit case' : 'Log a new case'}
        >
          <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Type</label>
              <select value={form.case_type} onChange={(e) => setForm({ ...form, case_type: e.target.value })} className={`w-full ${form.case_type ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}>
                {CASE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Severity</label>
              <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="w-full">
                {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Title / subject</label>
              <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required maxLength={200}
                className={`w-full ${form.title.trim() ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`} placeholder="Short summary of the matter" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Reported date</label>
              <input type="date" value={form.reported_date} onChange={(e) => setForm({ ...form, reported_date: e.target.value })}
                className="w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Resolved date {isResolvedState ? '' : '(auto when resolved)'}</label>
              <input type="date" value={form.resolved_date} onChange={(e) => setForm({ ...form, resolved_date: e.target.value })} disabled={!isResolvedState}
                className="w-full disabled:opacity-50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Amount involved (N$)</label>
              <input type="number" step="0.01" min="0" value={form.amount_involved} onChange={(e) => setForm({ ...form, amount_involved: e.target.value })}
                className="w-full" placeholder="e.g. overcharge amount" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Client ID or code (optional)</label>
              <input type="text" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                className="w-full" placeholder="Linked client — e.g. KFS-CL-000026" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Loan ID or code (optional)</label>
              <input type="text" value={form.loan_id} onChange={(e) => setForm({ ...form, loan_id: e.target.value })}
                className="w-full" placeholder="Linked loan — e.g. KFS-LN-000048" />
            </div>
            {form.case_type === 'Complaint' ? (
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Outcome (NAMFISA)</label>
                <select value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })}
                  className="w-full">
                  <option value="">— Not decided —</option>
                  {OUTCOMES.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ) : (
              <div className="hidden md:block" />
            )}

            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
                className="w-full" placeholder="What happened / details of the matter" />
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Resolution / action taken</label>
              <textarea value={form.resolution} onChange={(e) => setForm({ ...form, resolution: e.target.value })} rows={2}
                className="w-full" placeholder="How it was / will be resolved" />
            </div>

            <div className="md:col-span-3 flex items-center gap-2">
              <ActionButton
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-60"
                busyLabel="Saving…"
                onClick={(e) => {
                  const formEl = e.currentTarget.form;
                  if (formEl && !formEl.reportValidity()) { e.preventDefault(); return; }
                  return onSubmit(e);
                }}
              >
                {saving ? 'Saving…' : editingId ? 'Update case' : 'Save case'}
              </ActionButton>
              <button type="button" onClick={cancelForm} className="px-4 py-2 border border-gray-300 dark:border-slate-600 rounded">Cancel</button>
            </div>
          </form>
        </FullScreenPanel>
      )}

      {/* Filters */}
      <div className="p-3 rounded-lg bg-white dark:bg-slate-900 shadow-md">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-3">
          <input type="text" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search title, code, client…"
            className="toolbar-control w-full" />
          <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} className="toolbar-control w-full">
            <option value="">All types</option>
            {CASE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="toolbar-control w-full">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {(filters.q || filters.type || filters.status) && (
            <button type="button" onClick={() => setFilters({ type: '', status: '', q: '' })} className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300 self-center justify-self-start">Clear filters</button>
          )}
        </div>

        {!loading && cases.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <p className="text-sm text-gray-600 dark:text-slate-300">
              <span className="font-semibold text-gray-900 dark:text-slate-100">{cases.length}</span>
              {` case${cases.length === 1 ? '' : 's'}`}
            </p>
            <Pager page={pageSafe} pages={totalPages} onPage={setPage} className="ml-auto" />
          </div>
        )}

        {loading ? (
          <div className="text-gray-600 dark:text-slate-400 py-6 text-center">Loading cases…</div>
        ) : cases.length === 0 ? (
          <div className="text-gray-600 dark:text-slate-400 py-6 text-center">No compliance cases found.</div>
        ) : (
          <ResponsiveTable
            data={pagedCases}
            sort={caseSort.sort}
            onSortChange={caseSort.onSortChange}
            emptyMessage="No compliance cases found."
            columns={[
              { field: 'case_code', header: 'Code', formatter: (v) => <span className="font-mono text-xs text-gray-700 dark:text-slate-300">{v}</span> },
              { field: 'case_type', header: 'Type', formatter: (v) => <span className="text-gray-800 dark:text-slate-200">{v}</span> },
              { field: 'title', header: 'Title', formatter: (v) => <span className="font-medium">{v}</span> },
              {
                field: 'client_name',
                header: 'Client / Loan',
                formatter: (v, c) => (
                  <div className="text-xs text-gray-600 dark:text-slate-400">
                    {c.client_name ? <div><ClientLink clientId={c.client_id} name={c.client_name} />{c.client_code ? ` (${c.client_code})` : ''}</div> : null}
                    {c.loan_code ? <div>Loan <LoanLink loanId={c.loan_id}>{c.loan_code}</LoanLink></div> : null}
                    {!c.client_name && !c.loan_code ? '—' : null}
                  </div>
                ),
              },
              {
                field: 'severity',
                header: 'Severity',
                formatter: (v) => <span className={`px-2 py-1 rounded-full text-xs font-semibold ${SEVERITY_BADGE[v] || ''}`}>{v}</span>,
              },
              {
                field: 'status',
                header: 'Status',
                formatter: (v, c) => canManage ? (
                  <select value={c.status} onChange={(e) => quickStatus(c, e.target.value)}
                    className={`!text-xs font-semibold rounded-full !px-2 !py-0.5 border-0 cursor-pointer ${statusClasses(c.status).slice(1).join('')}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                ) : (
                  <StatusBadge status={c.status} />
                ),
              },
              { field: 'reported_date', header: 'Reported', formatter: (v) => <span className="text-gray-700 dark:text-slate-300">{fmt(v)}</span> },
              { field: 'resolved_date', header: 'Resolved', formatter: (v) => <span className="text-gray-700 dark:text-slate-300">{fmt(v)}</span> },
              {
                field: 'amount_involved',
                header: 'Amount (N$)',
                formatter: (v) => <span className="text-gray-700 dark:text-slate-300">{v != null ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</span>,
              },
            ]}
            actions={[
              {
                label: 'Edit',
                condition: () => canManage,
                onClick: (c) => startEdit(c),
                className: 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 transition-colors',
              },
              {
                label: 'Delete',
                condition: () => canManage,
                onClick: (c) => removeCase(c),
                className: 'bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300 transition-colors',
              },
            ]}
          />
        )}
      </div>
    </div>
  );
};

export default ComplianceCases;
